import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@asm/ui";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { DashboardLayout } from "./components/layouts/DashboardLayout";
import { AuthLayout } from "./components/layouts/AuthLayout";
import { FEATURES, type FeatureFlag } from "./lib/features";
import { schedulesApi, type TierLimitsResponse } from "./lib/api";

// Pages
import Dashboard from "./pages/Dashboard";
import Scans from "./pages/Scans";
import ScanDetails from "./pages/ScanDetails";
import ScanDetailsSimple from "./pages/ScanDetailsSimple";
import NewScan from "./pages/NewScan";
import Comparisons from "./pages/Comparisons";
import Schedules from "./pages/Schedules";
import ScheduleDetails from "./pages/ScheduleDetails";
import Domains from "./pages/Domains";
import AssetDetails from "./pages/AssetDetails";
import DomainAssets from "./pages/DomainAssets";
import Graph from "./pages/Graph";
import Settings from "./pages/Settings";
import PortScan from "./pages/PortScan";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ModuleDemo from "./pages/ModuleDemo";

// Asset Views (new nested structure)
import AssetsLayout from "./pages/assets/index";
import AssetInventory from "./pages/assets/AssetInventory";
import RiskDashboard from "./pages/assets/RiskDashboard";
import ServiceMatrix from "./pages/assets/ServiceMatrix";
import AttackPaths from "./pages/assets/AttackPaths";
import AssetTimeline from "./pages/assets/AssetTimeline";
import Exceptions from "./pages/assets/Exceptions";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Route guard for feature-flagged routes
 * Redirects to home if feature is disabled
 */
function FeatureRoute({
  feature,
  children,
}: {
  feature: FeatureFlag;
  children: React.ReactNode;
}) {
  if (!FEATURES[feature]) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Route guard for tier-gated features
 * Redirects to home if the org's tier doesn't include the feature
 */
function TierRoute({
  gate,
  children,
}: {
  gate: keyof Pick<TierLimitsResponse, "asset_inventory_enabled" | "webhooks_enabled">;
  children: React.ReactNode;
}) {
  const { data: tierLimits, isLoading } = useQuery({
    queryKey: ["tierLimits"],
    queryFn: () => schedulesApi.getTierLimits(),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return null;
  if (tierLimits && !tierLimits[gate]) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <ThemeProvider>
      <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
        </Route>

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/scans" element={<Scans />} />
          <Route path="/scans/new" element={<NewScan />} />
          <Route path="/scans/:id" element={<ScanDetailsSimple />} />
          <Route path="/scans/:id/simple" element={<ScanDetailsSimple />} />
          <Route path="/scans/:id/advanced" element={<ScanDetails />} />
          <Route
            path="/comparisons"
            element={
              <FeatureRoute feature="comparisons">
                <Comparisons />
              </FeatureRoute>
            }
          />
          <Route path="/schedules" element={<Schedules />} />
          <Route path="/schedules/:id" element={<ScheduleDetails />} />
          <Route path="/domains" element={<Domains />} />
          <Route path="/domains/:id/assets" element={<DomainAssets />} />
          {/* Asset Intelligence with nested views */}
          <Route path="/assets" element={<TierRoute gate="asset_inventory_enabled"><AssetsLayout /></TierRoute>}>
            <Route index element={<AssetInventory />} />
            <Route
              path="risks"
              element={
                <FeatureRoute feature="riskDashboard">
                  <RiskDashboard />
                </FeatureRoute>
              }
            />
            <Route
              path="matrix"
              element={
                <FeatureRoute feature="serviceMatrix">
                  <ServiceMatrix />
                </FeatureRoute>
              }
            />
            <Route
              path="paths"
              element={
                <FeatureRoute feature="attackPaths">
                  <AttackPaths />
                </FeatureRoute>
              }
            />
            <Route
              path="timeline"
              element={
                <FeatureRoute feature="timeline">
                  <AssetTimeline />
                </FeatureRoute>
              }
            />
            <Route
              path="graph"
              element={
                <FeatureRoute feature="graphVisualization">
                  <Graph />
                </FeatureRoute>
              }
            />
            <Route path="exceptions" element={<Exceptions />} />
            <Route path=":id" element={<AssetDetails />} />
          </Route>
          {/* Legacy graph route - redirect to new location */}
          <Route
            path="/graph"
            element={
              <FeatureRoute feature="graphVisualization">
                <Graph />
              </FeatureRoute>
            }
          />
          <Route path="/settings" element={<Settings />} />
          <Route path="/tools/portscan" element={<PortScan />} />
          <Route path="/demo/modules" element={<ModuleDemo />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </ThemeProvider>
  );
}

export default App;
